import { format } from 'date-fns';
import { de } from 'date-fns/locale';
import { Clock, Edit, MapPin, Trash2, User as UserIcon } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { CachedImage } from '@/components/ui/cached-image';
import { Card, CardHeader, CardTitle } from '@/components/ui/card';
import { getUserDisplayName } from '@/hooks/use-users';
import i18n from '@/i18n';
import { cn } from '@/lib/utils';
import type { User } from '@/services/api';
import type { Appointment } from '@/types/appointment';

export type AppointmentCardVariant = 'default' | 'assigned' | 'compact';

interface AppointmentCardProps {
  appointment: Appointment;
  variant?: AppointmentCardVariant;
  users?: Record<string, User>;
  currentUserId?: string;
  actions?: ReactNode;
  className?: string;
  showCreatedBy?: boolean;
  onEdit?: (appointment: Appointment) => void;
  onDelete?: (appointment: Appointment) => void;
}

export function AppointmentCard({
  appointment,
  variant = 'default',
  users = {},
  currentUserId,
  actions,
  className,
  showCreatedBy = true,
  onEdit,
  onDelete,
}: AppointmentCardProps) {
  const { t } = useTranslation();

  const getStatusBadge = (status: Appointment['status']) => {
    switch (status) {
      case 'assigned':
        return (
          <Badge variant="default" className="bg-green-500 text-white">
            {t('appointments.assigned')}
          </Badge>
        );
      case 'completed':
        return (
          <Badge variant="default" className="bg-blue-500 text-white">
            {t('appointments.completed')}
          </Badge>
        );
      default:
        return <Badge variant="destructive">{t('appointments.unassigned')}</Badge>;
    }
  };

  const formatAppointmentTime = (startTime: string, duration: number) => {
    const start = new Date(startTime);
    const end = new Date(start.getTime() + duration * 60 * 1000);

    if (variant === 'compact') {
      return `${format(start, 'HH:mm', { locale: i18n.language === 'de' ? de : undefined })} - ${format(end, 'HH:mm', { locale: i18n.language === 'de' ? de : undefined })}`;
    }

    return `${format(start, 'EEE, MMM d, yyyy - HH:mm', { locale: i18n.language === 'de' ? de : undefined })} (${duration} ${t('time.minutes')})`;
  };

  const cardClassName = cn(
    variant === 'assigned' && 'border-green-200 bg-green-50/50',
    variant === 'compact' && 'border-l-4 border-l-primary',
    className,
  );

  const titleClassName = cn(
    'text-base font-medium',
    variant === 'assigned' && 'text-green-900',
    variant === 'compact' && 'text-sm',
  );

  const textClassName = cn(
    'text-sm text-muted-foreground',
    variant === 'assigned' && 'text-green-700',
    variant === 'compact' && 'text-xs',
  );

  const defaultActions = (
    <div className="flex items-center gap-2 shrink-0">
      {onEdit && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onEdit(appointment)}
          className="border-blue-300 text-blue-600 hover:bg-blue-50 gap-1"
        >
          <Edit className="h-4 w-4" />
          {variant !== 'compact' && t('appointments.edit')}
        </Button>
      )}
      {onDelete && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDelete(appointment)}
          className="border-red-300 text-red-600 hover:bg-red-50 gap-1"
        >
          <Trash2 className="h-4 w-4" />
          {variant !== 'compact' && t('appointments.delete')}
        </Button>
      )}
      {actions}
    </div>
  );

  if (variant === 'compact') {
    return (
      <div className={cn('p-3 border rounded-lg space-y-2', cardClassName)}>
        <div className="flex items-start justify-between">
          <h3 className={titleClassName}>{appointment.title}</h3>
          {getStatusBadge(appointment.status)}
        </div>

        <div className="flex items-center gap-2">
          <Clock className="h-4 w-4" />
          <span className={textClassName}>{formatAppointmentTime(appointment.startTime, appointment.duration)}</span>
        </div>

        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4" />
          <span className={textClassName}>{appointment.location}</span>
        </div>

        <div className="flex items-center gap-2">
          <UserIcon className="h-4 w-4" />
          <span className={textClassName}>
            {t('common.driver')}:{' '}
            {appointment.assignedTo
              ? getUserDisplayName(users[appointment.assignedTo], appointment.assignedTo, currentUserId)
              : t('appointments.unassigned')}
          </span>
        </div>

        {actions}

        {appointment.description && (
          <div className="text-xs text-muted-foreground mt-2 pt-2 border-t">{appointment.description}</div>
        )}
      </div>
    );
  }

  return (
    <Card className={cardClassName}>
      <CardHeader className="pb-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="space-y-1">
            <CardTitle className={cn(titleClassName, 'flex items-center gap-2')}>
              {appointment.title}
              {variant === 'assigned' && (
                <Badge variant="default" className="bg-green-500 text-white">
                  {t('appointments.yourAppointment')}
                </Badge>
              )}
            </CardTitle>
            {getStatusBadge(appointment.status)}
          </div>
          {defaultActions}
        </div>

        <div className="flex flex-wrap items-center gap-4 mt-3">
          <span className={cn('flex items-center gap-1', textClassName)}>
            <MapPin className="h-4 w-4" />
            {appointment.location}
          </span>
          <span className={cn('flex items-center gap-1', textClassName)}>
            <Clock className="h-4 w-4" />
            {formatAppointmentTime(appointment.startTime, appointment.duration)}
          </span>
          {appointment.assignedTo && (
            <span className={cn('flex items-center gap-1', textClassName)}>
              <UserIcon className="h-4 w-4" />
              {getUserDisplayName(users[appointment.assignedTo], appointment.assignedTo, currentUserId)}
            </span>
          )}
        </div>

        {appointment.description && (
          <div
            className={cn(
              'text-sm mt-3 p-3 rounded border',
              variant === 'assigned'
                ? 'text-green-800 bg-green-100 border-green-200'
                : 'text-gray-800 bg-gray-100 border-gray-200',
            )}
          >
            {appointment.description}
          </div>
        )}

        {showCreatedBy && appointment.createdBy && appointment.createdBy !== currentUserId && (
          <div
            className={cn(
              'flex items-center gap-2 mt-3 p-2 rounded border',
              variant === 'assigned' ? 'bg-green-100 border-green-200' : 'bg-gray-100 border-gray-200',
            )}
          >
            {users[appointment.createdBy]?.image && (
              <CachedImage
                src={users[appointment.createdBy].image}
                alt={getUserDisplayName(users[appointment.createdBy], appointment.createdBy, currentUserId)}
                className="w-5 h-5 rounded-full"
              />
            )}
            <span className={cn('text-sm font-medium', variant === 'assigned' ? 'text-green-800' : 'text-gray-800')}>
              {t('appointments.createdBy')}{' '}
              {getUserDisplayName(users[appointment.createdBy], appointment.createdBy, currentUserId)}
            </span>
          </div>
        )}
      </CardHeader>
    </Card>
  );
}
